import {
  ClearPvHistory,
  DeletePv,
  GetPvByUserId,
  MutePv,
  Pv,
  reqClearPvHistory,
  reqDeletePv,
  reqGetPvByUserId,
  reqMutePv,
  reqSetPvUnreadMark,
  reqSyncPv,
  SetPvUnreadMark,
  SyncPv,
} from '@weblite/ts-action/pv'
import { removeMessagesByRange } from '$logic/message'
import { request, requestNow } from '$setup/ws'
import { nothing } from '$func/utils'
import { setSeenRoomAllMessages } from '$logic/room-seen'
import { clearRoomSharedMediaDB } from '$logic/shared-media/service'
import { updateRoomLastMessage } from '$logic/room-last-message'
import {
  showChatRemovedSnackbar,
  showHistoryWasClearedSnackbar,
} from '$comp/snackbars'
import * as roomService from '../room'
import { pvsStorage } from './storage'
import * as sync from './offline-sync'

export const getPv = pvsStorage.get
export const getAllPvs = pvsStorage.getAll
export const loadAndGetAllPv = pvsStorage.dbGetAll

export async function getPvByUserId(userId: string): Promise<Pv | undefined> {
  const localResult = await pvsStorage.findByUser(userId)
  if (localResult) return localResult
  const res = await request<GetPvByUserId>(reqGetPvByUserId({ userId }))
  return res.payload?.pv
}

export async function getUserIdsByPvIds(pvIds: string[] = []) {
  const pvs = await Promise.all(pvIds.map(getPv))
  return pvs.map(({ to }) => to)
}

export async function clearPvHistory(roomId: string): Promise<void> {
  const { iid } = await roomService.getRoom(roomId)
  const unsetSync = await sync.set(roomId, { firstVisibleIid: iid + 1 })
  await pvsStorage.set(roomId, { firstVisibleIid: iid + 1 })
  await updateRoomLastMessage(roomId)
  removeMessagesByRange(roomId, 1, iid)
  clearRoomSharedMediaDB(roomId)
  showHistoryWasClearedSnackbar()
  // NOTE: should resolve after changing in frontend for good offline experience. hence request has no await
  request<ClearPvHistory>(reqClearPvHistory({ iid, roomId })).then(
    async res => {
      await pvsStorage.set(roomId, { updatedAt: res.payload?.updatedAt })
      await unsetSync()
    },
  )
}

export async function deletePv(
  roomId: string,
  shouldClearHistory: boolean,
  disableSnackbar = false,
): Promise<void> {
  const { iid } = (await roomService.getRoom(roomId)) || {}
  // if (!iid) return
  const unsetDeleteRoomSync = await roomService.addToDeletedRooms(roomId, {
    type: 'PV',
    iid,
  })
  const unsetClearHistorySync = shouldClearHistory
    ? await sync.set(roomId, { firstVisibleIid: iid + 1 })
    : nothing
  await roomService.pruneRoom(roomId)
  if (shouldClearHistory) removeMessagesByRange(roomId, 1, iid)
  if (!disableSnackbar) showChatRemovedSnackbar()

  // NOTE: should resolve after changing in frontend for good offline experience. hence request has no await
  request<DeletePv>(
    reqDeletePv({ roomId, iid, clearHistory: shouldClearHistory }),
  ).then(async () => {
    await unsetDeleteRoomSync()
    await unsetClearHistorySync()
  })
}

// TODO: investigate more smooth view by switching following lines
export async function setPvMute(roomId: string, muted: boolean): Promise<void> {
  const unsetSync = await sync.set(roomId, { muted })
  await pvsStorage.set(roomId, { muted })

  // NOTE: should resolve after changing in frontend for good offline experience. hence request has no await
  request<MutePv>(reqMutePv({ roomId, value: muted })).then(async res => {
    await pvsStorage.set(roomId, { updatedAt: res.payload.updatedAt })
    await unsetSync()
  })
}

export async function setPvUnreadMark(roomId: string, unreadMark: boolean) {
  const pv = await getPv(roomId)
  if (pv.unreadMark === unreadMark) return

  const unsetSync = await sync.set(roomId, { unreadMark })
  await pvsStorage.set(roomId, { unreadMark })

  // NOTE: should resolve after changing in frontend for good offline experience. hence request has no await
  request<SetPvUnreadMark>(
    reqSetPvUnreadMark({ roomId, value: unreadMark }),
  ).then(async res => {
    await pvsStorage.set(roomId, { updatedAt: res.payload.updatedAt })
    await unsetSync()
  })
}

export async function markPvAsRead(roomId: string): Promise<void> {
  await setPvUnreadMark(roomId, false)

  await setSeenRoomAllMessages(roomId)
}

export async function syncPv(): Promise<void> {
  const { items, del, release } = await sync.getAll()
  if (!items.length) {
    release()
    return
  }

  try {
    const pvs = items.map(item => ({ roomId: item.id, ...item.patch }))
    const res = await requestNow<SyncPv>(reqSyncPv({ pvs }))

    await Promise.all(
      res.payload.pvs.map(({ roomId, ...fields }) =>
        pvsStorage.set(roomId, fields).then(() => {
          if (
            fields &&
            ('firstVisibleIid' in fields || 'lastVisibleIid' in fields)
          )
            updateRoomLastMessage(roomId)
        }),
      ),
    )
    await del()
  } finally {
    release()
  }
}
